import { unstable_cache } from 'next/cache';
import { NextRequest } from 'next/server';

import { getTrackerId } from '@bytel/logger-nextjs/server';

import { appContext } from '@services/app-process';
import { getAppConfig } from '@services/config';

import { BytelAxiosRequestConfig, Callback } from '@app-types/axios';
import { UserType } from '@app-types/user';

import { isServer } from '@helpers/config';
import { getClientCookies } from '@helpers/cookies-client';

import { API_X_SOURCE, SAPI_X_VERSION } from '@constants/api';

import { AxiosInstance, AxiosResponse } from 'axios';
import { createHash } from 'crypto';
import { v4 as uuid } from 'uuid';

import { HttpConfig, HttpService } from './http';

type ExtendedRequestConfig = BytelAxiosRequestConfig;

const appConfig = getAppConfig();

export class AxiosService extends HttpService {
    private readonly isApi: boolean;
    private readonly isCms: boolean;
    private httpInstance: AxiosInstance;
    private headers: Record<string, string> = {};

    constructor(axiosInstance: AxiosInstance, isApi: boolean = false, isCms: boolean = false) {
        super();
        this.httpInstance = axiosInstance;
        this.isApi = isApi;
        this.isCms = isCms;
    }

    public setInitialHeaders(headers: Headers): void {
        this.headers = Object.fromEntries(headers.entries());
    }

    public fusionHeaders(request: NextRequest, headers: Record<string, string> = {}): Record<string, string> {
        return {
            'x-request-id': request.headers.get('x-request-id') ?? '',
            trackerId: request.headers.get('trackerId') ?? '',
            ...headers,
        };
    }

    public async get<T>(url: string, data: object = {}, httpConfig: HttpConfig = {}): Promise<T> {
        const config = await this.initConfig(httpConfig);
        const cacheKey = this.generateCacheKey(url, data, httpConfig.user);

        config.params = data;

        return await this.launchRequest(
            async () => {
                return this.httpInstance.get(url, config).then((response) => this.processResponse<T>(response));
            },
            cacheKey,
            config,
        )();
    }

    public async post<T>(url: string, data?: object, httpConfig: HttpConfig = {}): Promise<T> {
        const config = await this.initConfig(httpConfig);
        const cacheKey = this.generateCacheKey(url, data, httpConfig.user);

        return await this.launchRequest(
            async () => {
                return this.httpInstance
                    .post<T>(url, data, config)
                    .then((response) => this.processResponse<T>(response));
            },
            cacheKey,
            config,
        )();
    }

    private launchRequest<T extends Callback>(cb: T, key: string, config: ExtendedRequestConfig): T {
        if (!config.next?.revalidate || !isServer()) {
            return cb;
        }

        return unstable_cache(cb, [key], { tags: config?.next?.tags, revalidate: config?.next?.revalidate });
    }

    protected processResponse<T>(response: AxiosResponse): T {
        return response.data;
    }

    protected async initConfig(httpConfig: HttpConfig = {}): Promise<ExtendedRequestConfig> {
        let headersObject = httpConfig.headers ?? {};

        const config: ExtendedRequestConfig = {
            signal: httpConfig?.controller ? httpConfig.controller.signal : undefined,
            timeout: 15000,
        };

        if (httpConfig.user) {
            config.user = {
                idPersonne: httpConfig.user.sub,
                login: httpConfig.user.login,
                userType: httpConfig.user.user_type,
            };
        }

        if (httpConfig.ttl !== undefined) {
            config.next = {
                ...config.next,
                revalidate: httpConfig.ttl * 60,
            };
        }

        if (httpConfig?.tags && httpConfig.tags.length > 0) {
            config.next = {
                ...config.next,
                tags: httpConfig.tags,
            };
        }

        if (!this.isCms) {
            headersObject = {
                ...headersObject,
                'content-type': 'application/json',
            };
        } else {
            config.next = {
                ...config.next,
                mute: true,
            };
        }

        headersObject = await this.setHeadersApi(httpConfig, headersObject);
        headersObject = this.cleanHeaders(headersObject);

        return { ...config, headers: headersObject };
    }

    private async setHeadersApi(
        httpConfig: HttpConfig,
        baseHeaders: Record<string, string>,
    ): Promise<Record<string, string>> {
        const headers = { ...baseHeaders };
        if (!headers['x-source']) {
            headers['x-source'] = API_X_SOURCE;
        }

        if (!headers['x-process']) {
            headers['x-process'] = appContext.getProcess();
        }
        headers['x-request-id'] ??= uuid();

        const trackerId = isServer() ? await getTrackerId() : getClientCookies('bytel_logger_trackerId');
        if (trackerId) {
            headers['trackerId'] = trackerId;
        }

        if (this.isApi) {
            if (!headers['x-version']) {
                headers['x-version'] = SAPI_X_VERSION.V4;
            }

            headers['accept-encoding'] = 'gzip';

            const trueClientIp = this.headers['true-client-ip'];

            if (trueClientIp) {
                headers['x-real-ip'] = trueClientIp;
                headers['x-bytel-ip'] = trueClientIp;
                headers['x-last-ip'] = trueClientIp;
            }

            if (appConfig.bench && !headers['x-banc']) {
                headers['x-banc'] = appConfig.bench;
            }

            if (httpConfig.user) {
                headers['authorization'] = `Bearer ${httpConfig.user.accessToken}`;
            }
        }

        return headers;
    }

    private cleanHeaders(headers: Record<string, string>): Record<string, string> {
        delete headers['host'];
        delete headers['accept'];
        delete headers['accept-language'];
        delete headers['connection'];
        delete headers['content-length'];
        delete headers['origin'];
        return headers;
    }

    private generateCacheKey(url: string, data?: object, user?: UserType): string {
        const dataString = data ? JSON.stringify(data) : '';
        const userString = user ? JSON.stringify(user.login) : '';
        const rawKey = `${url}-${dataString}-${userString}`;
        return createHash('sha256').update(rawKey).digest('hex');
    }
}
